import Backbone from "backbone";
import Recorder from "libs/browserRecorder/recorderNew";
import BrowserRecordingDraftStore, {
    BrowserRecordingBinaryStoreClient,
    STORAGE_BACKENDS as RECORDING_DRAFT_STORAGE_BACKENDS,
    STATUSES as RECORDING_DRAFT_STATUSES
} from "models/helper/BrowserRecordingDraftStore";

const RECORDING_TIMESLICE_IN_MILLISECONDS = 2000;
const AUDIO_RECORDING_BITS_PER_SECOND = 128000;
const VIDEO_RECORDING_AUDIO_BITS_PER_SECOND = 128000;
const MIN_VIDEO_RECORDING_BITS_PER_SECOND = 2500000;
const MAX_VIDEO_RECORDING_BITS_PER_SECOND = 45000000;
const VIDEO_RECORDING_BITS_PER_PIXEL_PER_FRAME = 0.12;
const VIDEO_RECORDING_DEFAULT_FRAME_RATE = 30;
const VIDEO_COMPOSER_TARGET_FRAME_RATE = 30;
const VIDEO_COMPOSER_MAX_SOURCE_DRIFT_IN_MILLISECONDS = 750;
const AUDIO_STABILIZER_START_TIMEOUT_IN_MILLISECONDS = 1000;
const VIDEO_COMPOSER_LOSS_REASON_SOURCE_DRIFT = "source_video_drift";
const VIDEO_COMPOSER_LOSS_REASON_NO_DRAWABLE_FRAME = "source_video_unavailable";
const MEDIA_TRACK_LOSS_REASON_MUTED = "track_muted";
const MEDIA_TRACK_LOSS_REASON_ENDED = "track_ended";
const DEFAULT_VIDEO_CONSTRAINTS = {
    facingMode: "user",
    width: { ideal: 1280 },
    height: { ideal: 720 }
};

const getSupportedRecorderMimeInfo = function(mediaType) {
    if (typeof window === "undefined" || !("MediaRecorder" in window)) {
        return null;
    }

    const mimeTypesToTest = mediaType === "video" ? [
        "video/webm;codecs=vp8,opus",
        "video/webm",
        "video/webm;codecs=vp9,opus",
        "video/mp4"
    ] : [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/wav",
        "audio/mp4"
    ];

    for (let index = 0; index < mimeTypesToTest.length; index++) {
        const mimeType = mimeTypesToTest[index];
        if (MediaRecorder.isTypeSupported(mimeType)) {
            return mimeType;
        }
    }

    return null;
};

const isTrackLive = function(track) {
    return !!track && track.readyState !== "ended";
};

const clampNumber = function(value, minimumValue, maximumValue) {
    return Math.min(maximumValue, Math.max(minimumValue, value));
};

const getFirstLiveTrackForType = function(stream, trackType) {
    if (!stream) {
        return null;
    }

    const getTracks = trackType === "video" ? stream.getVideoTracks : stream.getAudioTracks;
    if (!getTracks) {
        return null;
    }

    const tracks = getTracks.call(stream) || [];
    for (let index = 0; index < tracks.length; index++) {
        if (isTrackLive(tracks[index])) {
            return tracks[index];
        }
    }

    return null;
};

const getVideoBitsPerSecondForStream = function(stream) {
    const videoTrack = getFirstLiveTrackForType(stream, "video");
    const settings = videoTrack && videoTrack.getSettings ? videoTrack.getSettings() : {};
    const width = Math.max(Number(settings.width) || 0, 0);
    const height = Math.max(Number(settings.height) || 0, 0);
    const resolvedFrameRate = Number(settings.frameRate);
    const frameRate = resolvedFrameRate > 0 ? resolvedFrameRate : VIDEO_RECORDING_DEFAULT_FRAME_RATE;

    if (!width || !height) {
        return null;
    }

    return Math.round(clampNumber(
        width * height * frameRate * VIDEO_RECORDING_BITS_PER_PIXEL_PER_FRAME,
        MIN_VIDEO_RECORDING_BITS_PER_SECOND,
        MAX_VIDEO_RECORDING_BITS_PER_SECOND
    ));
};

const buildMediaRecorderOptions = function(mimeType, mediaType, stream) {
    const options = { mimeType: mimeType };

    if (mediaType === "video") {
        const videoBitsPerSecond = getVideoBitsPerSecondForStream(stream);
        if (videoBitsPerSecond) {
            options.videoBitsPerSecond = videoBitsPerSecond;
        }

        if (getFirstLiveTrackForType(stream, "audio")) {
            options.audioBitsPerSecond = VIDEO_RECORDING_AUDIO_BITS_PER_SECOND;
        }
    } else {
        options.audioBitsPerSecond = AUDIO_RECORDING_BITS_PER_SECOND;
    }

    return options;
};

const createMediaRecorderForStream = function(stream, options) {
    try {
        return new MediaRecorder(stream, options);
    } catch (error) {
        if (!options || !options.mimeType || (!options.audioBitsPerSecond && !options.videoBitsPerSecond)) {
            throw error;
        }

        console.warn("Unable to initialize MediaRecorder with bitrate options; retrying with MIME type only.", error);
        return new MediaRecorder(stream, { mimeType: options.mimeType });
    }
};

const stopMediaStreamTracks = function(stream) {
    if (!stream || !stream.getTracks) {
        return;
    }

    stream.getTracks().forEach(function(track) {
        try {
            track.stop();
        } catch (_) {
            // Ignore track cleanup failures.
        }
    });
};

const stopDetachedMediaStreamTracks = function(stream, activeStream) {
    if (!stream || !stream.getTracks) {
        return;
    }

    const activeTracks = activeStream && activeStream.getTracks ? activeStream.getTracks() : [];
    stream.getTracks().forEach(function(track) {
        if (activeTracks.indexOf(track) >= 0) {
            return;
        }

        try {
            track.stop();
        } catch (_) {
            // Ignore track cleanup failures.
        }
    });
};

const waitForPromisesToSettle = function(promises) {
    return Promise.all((promises || []).map(function(promise) {
        return Promise.resolve(promise).then(function(value) {
            return {
                status: "fulfilled",
                value: value
            };
        }).catch(function(reason) {
            return {
                status: "rejected",
                reason: reason
            };
        });
    }));
};

class BrowserMediaTrackLossMonitor {
    constructor(sourceStream) {
        this._sourceStream = null;
        this._startedAt = null;
        this._trackEventCleanups = [];
        this._lossRegions = {
            audio: [],
            video: []
        };
        this._activeLossRegionsByTrackKey = {};
        this.replaceSourceStream(sourceStream);
    }

    start(startedAt) {
        this._startedAt = startedAt || Date.now();
        this._openInitialTrackLossRegions();
    }

    replaceSourceStream(sourceStream) {
        this._closeAllActiveLossRegions();
        this._detachTrackEventListeners();
        this._sourceStream = sourceStream || null;
        this._attachTrackEventListeners();
        if (this._startedAt) {
            this._openInitialTrackLossRegions();
        }
    }

    _getElapsedMs() {
        if (!this._startedAt) {
            return 0;
        }
        return Math.max(Date.now() - this._startedAt, 0);
    }

    _getTrackKey(kind, track, index) {
        return kind + ":" + ((track && track.id) || index || "unknown");
    }

    _attachTrackEventListeners() {
        if (!this._sourceStream || !this._sourceStream.getTracks) {
            return;
        }

        this._sourceStream.getTracks().forEach((track, index) => {
            if (!(track && (track.kind === "audio" || track.kind === "video") && track.addEventListener)) {
                return;
            }

            const kind = track.kind;
            const trackKey = this._getTrackKey(kind, track, index);
            const handleMute = () => {
                this._openLossRegion(kind, trackKey, track, MEDIA_TRACK_LOSS_REASON_MUTED);
            };
            const handleUnmute = () => {
                this._closeLossRegion(trackKey);
            };
            const handleEnded = () => {
                this._openLossRegion(kind, trackKey, track, MEDIA_TRACK_LOSS_REASON_ENDED);
            };

            track.addEventListener("mute", handleMute);
            track.addEventListener("unmute", handleUnmute);
            track.addEventListener("ended", handleEnded);
            this._trackEventCleanups.push(function() {
                track.removeEventListener("mute", handleMute);
                track.removeEventListener("unmute", handleUnmute);
                track.removeEventListener("ended", handleEnded);
            });
        });
    }

    _detachTrackEventListeners() {
        this._trackEventCleanups.forEach(function(cleanup) {
            cleanup();
        });
        this._trackEventCleanups = [];
    }

    _openInitialTrackLossRegions() {
        if (!this._sourceStream || !this._sourceStream.getTracks) {
            return;
        }

        this._sourceStream.getTracks().forEach((track, index) => {
            if (!(track && (track.kind === "audio" || track.kind === "video"))) {
                return;
            }
            const trackKey = this._getTrackKey(track.kind, track, index);
            if (track.readyState === "ended") {
                this._openLossRegion(track.kind, trackKey, track, MEDIA_TRACK_LOSS_REASON_ENDED);
            } else if (track.muted) {
                this._openLossRegion(track.kind, trackKey, track, MEDIA_TRACK_LOSS_REASON_MUTED);
            }
        });
    }

    _openLossRegion(kind, trackKey, track, reason) {
        const currentElapsedMs = this._getElapsedMs();
        const activeRegion = this._activeLossRegionsByTrackKey[trackKey];
        if (activeRegion) {
            activeRegion.endMs = currentElapsedMs;
            if (reason === MEDIA_TRACK_LOSS_REASON_ENDED) {
                activeRegion.reason = reason;
            }
            return;
        }

        const region = {
            startMs: currentElapsedMs,
            endMs: currentElapsedMs,
            reason: reason,
            trackId: track && track.id ? track.id : null
        };
        this._activeLossRegionsByTrackKey[trackKey] = region;
        this._lossRegions[kind].push(region);
    }

    _closeLossRegion(trackKey) {
        const activeRegion = this._activeLossRegionsByTrackKey[trackKey];
        if (!activeRegion) {
            return;
        }
        activeRegion.endMs = Math.max(this._getElapsedMs(), activeRegion.startMs);
        delete this._activeLossRegionsByTrackKey[trackKey];
    }

    _closeAllActiveLossRegions() {
        Object.keys(this._activeLossRegionsByTrackKey).forEach((trackKey) => {
            this._closeLossRegion(trackKey);
        });
    }

    getHealth() {
        Object.keys(this._activeLossRegionsByTrackKey).forEach((trackKey) => {
            const activeRegion = this._activeLossRegionsByTrackKey[trackKey];
            if (activeRegion) {
                activeRegion.endMs = Math.max(this._getElapsedMs(), activeRegion.startMs);
            }
        });

        return {
            audioLossRegions: this._lossRegions.audio.map(function(region) {
                return Object.assign({}, region);
            }),
            videoLossRegions: this._lossRegions.video.map(function(region) {
                return Object.assign({}, region);
            })
        };
    }

    finalize() {
        this._closeAllActiveLossRegions();
        return this.getHealth();
    }

    clear() {
        this._closeAllActiveLossRegions();
        this._detachTrackEventListeners();
        this._sourceStream = null;
    }
}

class BrowserAudioTrackStabilizer {
    constructor(sourceStream) {
        if (typeof window === "undefined" || !(window.AudioContext || window.webkitAudioContext) || typeof MediaStream !== "function") {
            throw new Error("BrowserAudioTrackStabilizer requires Web Audio and MediaStream support.");
        }

        this._sourceStream = sourceStream;
        this._audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this._destination = this._audioContext.createMediaStreamDestination();
        this._sourceNode = null;
        this._outputTrack = this._destination.stream.getAudioTracks()[0] || null;
        this._recordingStream = new MediaStream();
        this._sourceAudioTrackCount = 0;
        this._sourceConnected = false;

        if (sourceStream && sourceStream.getVideoTracks) {
            sourceStream.getVideoTracks().forEach((track) => {
                if (isTrackLive(track)) {
                    this._recordingStream.addTrack(track);
                }
            });
        }
        if (this._outputTrack) {
            this._recordingStream.addTrack(this._outputTrack);
        }

        const liveAudioTracks = sourceStream && sourceStream.getAudioTracks ? sourceStream.getAudioTracks().filter(isTrackLive) : [];
        this._sourceAudioTrackCount = liveAudioTracks.length;
        if (liveAudioTracks.length > 0) {
            this._sourceNode = this._audioContext.createMediaStreamSource(new MediaStream(liveAudioTracks));
            this._sourceNode.connect(this._destination);
            this._sourceConnected = true;
        }
    }

    getRecordingStream() {
        return this._recordingStream;
    }

    getSourceStream() {
        return this._sourceStream;
    }

    resume() {
        if (this._audioContext && this._audioContext.state === "suspended" && this._audioContext.resume) {
            return this._audioContext.resume();
        }
        return Promise.resolve();
    }

    isRunning() {
        return !this._audioContext || this._audioContext.state === "running";
    }

    getHealth() {
        return {
            enabled: true,
            silencePaddingEnabled: true,
            sourceAudioTrackCount: this._sourceAudioTrackCount,
            sourceConnected: this._sourceConnected,
            outputAudioTrackLive: isTrackLive(this._outputTrack),
            audioContextState: this._audioContext ? this._audioContext.state : null
        };
    }

    clear(options) {
        const clearOptions = options || {};
        if (this._sourceNode) {
            try {
                this._sourceNode.disconnect();
            } catch (_) {
                // Ignore disconnect failures during teardown.
            }
            this._sourceNode = null;
        }

        if (this._outputTrack) {
            try {
                this._outputTrack.stop();
            } catch (_) {
                // Ignore output track cleanup failures.
            }
            this._outputTrack = null;
        }

        if (!clearOptions.preserveSourceStream) {
            stopMediaStreamTracks(this._sourceStream);
        }
        this._sourceStream = null;

        if (this._audioContext && this._audioContext.close) {
            this._audioContext.close().catch(function() {
                // Ignore async close failures during teardown.
            });
        }
        this._audioContext = null;
    }
}

class BrowserVideoRecordingComposer {
    constructor(sourceStream) {
        if (typeof document === "undefined" || typeof document.createElement !== "function" || typeof MediaStream !== "function") {
            throw new Error("BrowserVideoRecordingComposer requires a browser DOM.");
        }

        this._sourceStream = null;
        this._recordingStream = new MediaStream();
        this._videoEl = document.createElement("video");
        this._canvasEl = document.createElement("canvas");
        this._canvasContext = this._canvasEl.getContext("2d", {
            alpha: false
        });
        this._animationFrameId = null;
        this._currentFrameCallbackId = null;
        this._pendingSourceFrameCallbackId = null;
        this._videoMountedToDom = false;
        this._sourceFirstMediaTimeMs = null;
        this._sourceFirstWallTimeMs = null;
        this._latestSourceFrameMediaTimeMs = null;
        this._latestSourceFrameWallTimeMs = null;
        this._sourceDriftMs = 0;
        this._renderStartedAtMs = null;
        this._drawnFrameCount = 0;
        this._frozenFrameCount = 0;
        this._videoLossRegions = [];
        this._activeVideoLossRegion = null;
        this._canvasEl.width = 1280;
        this._canvasEl.height = 720;
        if (!this._canvasEl.captureStream) {
            throw new Error("BrowserVideoRecordingComposer requires canvas captureStream support.");
        }
        this._videoEl.muted = true;
        this._videoEl.autoplay = true;
        this._videoEl.playsInline = true;
        this._videoEl.setAttribute("aria-hidden", "true");
        this._videoEl.style.position = "fixed";
        this._videoEl.style.left = "-99999px";
        this._videoEl.style.top = "0";
        this._videoEl.style.width = "1px";
        this._videoEl.style.height = "1px";
        this._videoEl.style.opacity = "0";
        this._videoEl.style.pointerEvents = "none";
        this._videoEl.style.zIndex = "-1";

        if (typeof document !== "undefined" && document.body) {
            document.body.appendChild(this._videoEl);
            this._videoMountedToDom = true;
        }

        const canvasStream = this._canvasEl.captureStream(VIDEO_COMPOSER_TARGET_FRAME_RATE);
        this._canvasTrack = canvasStream && canvasStream.getVideoTracks ? canvasStream.getVideoTracks()[0] : null;
        if (this._canvasTrack) {
            this._recordingStream.addTrack(this._canvasTrack);
        }

        if (sourceStream && sourceStream.getAudioTracks) {
            sourceStream.getAudioTracks().forEach((track) => {
                if (isTrackLive(track)) {
                    this._recordingStream.addTrack(track);
                }
            });
        }

        this.replaceSourceStream(sourceStream, {
            stopPreviousSource: false
        });
    }

    getRecordingStream() {
        return this._recordingStream;
    }

    _getNowMs() {
        return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
    }

    _resetSourceTiming() {
        this._sourceFirstMediaTimeMs = null;
        this._sourceFirstWallTimeMs = null;
        this._latestSourceFrameMediaTimeMs = null;
        this._latestSourceFrameWallTimeMs = null;
        this._sourceDriftMs = 0;
    }

    _stopSourceFrameMonitor() {
        if (this._currentFrameCallbackId !== null && this._videoEl && this._videoEl.cancelVideoFrameCallback) {
            this._videoEl.cancelVideoFrameCallback(this._currentFrameCallbackId);
        }
        this._currentFrameCallbackId = null;
    }

    _startSourceFrameMonitor() {
        this._stopSourceFrameMonitor();
        this._resetSourceTiming();
        if (!this._videoEl || !this._videoEl.requestVideoFrameCallback) {
            return;
        }

        const handleFrame = (now, metadata) => {
            this._currentFrameCallbackId = null;
            const mediaTimeMs = metadata && typeof metadata.mediaTime === "number" ? metadata.mediaTime * 1000 : null;
            const wallTimeMs = metadata && typeof metadata.expectedDisplayTime === "number" ? metadata.expectedDisplayTime : now;

            if (mediaTimeMs !== null) {
                if (this._sourceFirstMediaTimeMs === null || this._sourceFirstWallTimeMs === null) {
                    this._sourceFirstMediaTimeMs = mediaTimeMs;
                    this._sourceFirstWallTimeMs = wallTimeMs;
                }

                this._latestSourceFrameMediaTimeMs = mediaTimeMs;
                this._latestSourceFrameWallTimeMs = wallTimeMs;
                const sourceElapsedMs = mediaTimeMs - this._sourceFirstMediaTimeMs;
                const wallElapsedMs = wallTimeMs - this._sourceFirstWallTimeMs;
                this._sourceDriftMs = wallElapsedMs - sourceElapsedMs;
            }

            if (this._videoEl && this._videoEl.requestVideoFrameCallback) {
                this._currentFrameCallbackId = this._videoEl.requestVideoFrameCallback(handleFrame);
            }
        };

        this._currentFrameCallbackId = this._videoEl.requestVideoFrameCallback(handleFrame);
    }

    _attachSourceTrackToVideoElement(sourceStream) {
        const nextVideoTrack = sourceStream && sourceStream.getVideoTracks ? sourceStream.getVideoTracks().find(isTrackLive) : null;
        if (!nextVideoTrack) {
            throw new Error("No live video track was made available for the requested source.");
        }

        this.stopRenderingLoop();
        this._stopSourceFrameMonitor();
        this._resetSourceTiming();
        if (this._videoEl && this._videoEl.pause) {
            this._videoEl.pause();
        }
        if (this._videoEl) {
            this._videoEl.srcObject = null;
            if (this._videoEl.removeAttribute) {
                this._videoEl.removeAttribute("src");
            }
            if (this._videoEl.load) {
                this._videoEl.load();
            }
            this._videoEl.srcObject = new MediaStream([nextVideoTrack]);
        }
    }

    replaceSourceStream(sourceStream, options) {
        const replaceOptions = options || {};
        const previousSourceStream = this._sourceStream;

        this._sourceStream = sourceStream;
        try {
            this._attachSourceTrackToVideoElement(sourceStream);
        } catch (error) {
            this._sourceStream = previousSourceStream || null;
            return Promise.reject(error);
        }

        return this._playCurrentSource().then(() => {
            if (replaceOptions.stopPreviousSource !== false && previousSourceStream && previousSourceStream !== sourceStream) {
                stopDetachedMediaStreamTracks(previousSourceStream, this._recordingStream);
            }
            return this._recordingStream;
        }).catch((error) => {
            const restorePreviousSourcePromise = previousSourceStream && previousSourceStream !== sourceStream ? Promise.resolve().then(() => {
                this._sourceStream = previousSourceStream;
                this._attachSourceTrackToVideoElement(previousSourceStream);
                return this._playCurrentSource();
            }).catch((restoreError) => {
                console.warn("Unable to restore the previous video source after a failed replacement", restoreError);
                this._sourceStream = previousSourceStream || null;
                return null;
            }) : Promise.resolve().then(() => {
                if (this._sourceStream === sourceStream) {
                    this._sourceStream = previousSourceStream || null;
                }
                return null;
            });

            return restorePreviousSourcePromise.then(() => {
                throw error;
            });
        });
    }

    _playCurrentSource() {
        return new Promise((resolve, reject) => {
            let resolved = false;
            let readyTimeoutId = null;
            let firstFramePollFrameId = null;

            const clearPendingFirstFrameWait = () => {
                if (this._pendingSourceFrameCallbackId !== null && this._videoEl && this._videoEl.cancelVideoFrameCallback) {
                    this._videoEl.cancelVideoFrameCallback(this._pendingSourceFrameCallbackId);
                }
                if (firstFramePollFrameId !== null) {
                    window.cancelAnimationFrame(firstFramePollFrameId);
                }
                this._pendingSourceFrameCallbackId = null;
                firstFramePollFrameId = null;
            };

            const cleanup = () => {
                if (readyTimeoutId !== null) {
                    window.clearTimeout(readyTimeoutId);
                    readyTimeoutId = null;
                }
                clearPendingFirstFrameWait();
                this._videoEl.removeEventListener("loadedmetadata", handleSourceBecameReady);
                this._videoEl.removeEventListener("loadeddata", handleSourceBecameReady);
                this._videoEl.removeEventListener("canplay", handleSourceBecameReady);
                this._videoEl.removeEventListener("playing", handleSourceBecameReady);
            };

            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                cleanup();
                this._syncCanvasSize();
                if (this._renderStartedAtMs === null) {
                    this._renderStartedAtMs = this._getNowMs();
                }
                this._drawCurrentFrame();
                this._startSourceFrameMonitor();
                this._startRenderLoop();
                resolve(this._recordingStream);
            };

            const fail = (error) => {
                if (resolved) {
                    return;
                }
                resolved = true;
                cleanup();
                reject(error);
            };

            const hasDrawableFrame = () => {
                return !!(this._videoEl && this._videoEl.readyState >= 2 && this._videoEl.videoWidth > 0 && this._videoEl.videoHeight > 0);
            };

            const waitForFirstFrame = () => {
                if (resolved) {
                    return;
                }

                if (hasDrawableFrame()) {
                    finish();
                    return;
                }

                if (this._videoEl && this._videoEl.requestVideoFrameCallback) {
                    this._pendingSourceFrameCallbackId = this._videoEl.requestVideoFrameCallback(() => {
                        this._pendingSourceFrameCallbackId = null;
                        if (hasDrawableFrame()) {
                            finish();
                            return;
                        }
                        waitForFirstFrame();
                    });
                    return;
                }

                firstFramePollFrameId = window.requestAnimationFrame(() => {
                    firstFramePollFrameId = null;
                    if (hasDrawableFrame()) {
                        finish();
                        return;
                    }
                    waitForFirstFrame();
                });
            };

            const handleSourceBecameReady = () => {
                waitForFirstFrame();
            };

            this._videoEl.addEventListener("loadedmetadata", handleSourceBecameReady);
            this._videoEl.addEventListener("loadeddata", handleSourceBecameReady);
            this._videoEl.addEventListener("canplay", handleSourceBecameReady);
            this._videoEl.addEventListener("playing", handleSourceBecameReady);
            readyTimeoutId = window.setTimeout(function() {
                fail(new Error("Timed out while waiting for the new video source to produce a frame."));
            }, 2500);

            const playPromise = this._videoEl.play ? this._videoEl.play() : null;
            if (playPromise && playPromise.catch) {
                playPromise.catch(function(error) {
                    fail(error || new Error("Unable to play the new video source."));
                });
            }

            waitForFirstFrame();
        });
    }

    _syncCanvasSize() {
        const videoWidth = this._videoEl.videoWidth || 1280;
        const videoHeight = this._videoEl.videoHeight || 720;

        if (this._canvasEl.width !== videoWidth) {
            this._canvasEl.width = videoWidth;
        }
        if (this._canvasEl.height !== videoHeight) {
            this._canvasEl.height = videoHeight;
        }
    }

    _isSourceVideoFrameStalled() {
        if (!this._videoEl || !this._videoEl.requestVideoFrameCallback || this._renderStartedAtMs === null) {
            return false;
        }

        const nowMs = this._getNowMs();
        if (this._latestSourceFrameWallTimeMs !== null) {
            return nowMs - this._latestSourceFrameWallTimeMs > VIDEO_COMPOSER_MAX_SOURCE_DRIFT_IN_MILLISECONDS;
        }

        return nowMs - this._renderStartedAtMs > VIDEO_COMPOSER_MAX_SOURCE_DRIFT_IN_MILLISECONDS;
    }

    _drawCurrentFrame() {
        if (!this._canvasContext || !this._videoEl || this._videoEl.readyState < 2 || !(this._videoEl.videoWidth > 0) || !(this._videoEl.videoHeight > 0)) {
            this._freezeLastFrame(VIDEO_COMPOSER_LOSS_REASON_NO_DRAWABLE_FRAME);
            return;
        }

        this._syncCanvasSize();
        if (this._isSourceVideoFrameStalled()) {
            this._freezeLastFrame(VIDEO_COMPOSER_LOSS_REASON_NO_DRAWABLE_FRAME);
            return;
        }

        if (Math.abs(this._sourceDriftMs) > VIDEO_COMPOSER_MAX_SOURCE_DRIFT_IN_MILLISECONDS) {
            this._freezeLastFrame(VIDEO_COMPOSER_LOSS_REASON_SOURCE_DRIFT);
            return;
        }

        this._closeVideoLossRegion();
        this._canvasContext.clearRect(0, 0, this._canvasEl.width, this._canvasEl.height);
        this._canvasContext.drawImage(this._videoEl, 0, 0, this._canvasEl.width, this._canvasEl.height);
        this._drawnFrameCount = this._drawnFrameCount + 1;
    }

    _freezeLastFrame(reason) {
        if (!this._canvasContext) {
            return;
        }

        this._openVideoLossRegion(reason);
        if (this._canvasTrack && this._canvasTrack.requestFrame) {
            try {
                this._canvasTrack.requestFrame();
            } catch (_) {
                // Ignore manual frame request failures.
            }
        }
        this._frozenFrameCount = this._frozenFrameCount + 1;
    }

    _getElapsedMsForVideoLossRegion() {
        if (this._renderStartedAtMs !== null) {
            return Math.max(this._getNowMs() - this._renderStartedAtMs, 0);
        }
        if (this._sourceFirstWallTimeMs !== null && this._latestSourceFrameWallTimeMs !== null) {
            return Math.max(this._latestSourceFrameWallTimeMs - this._sourceFirstWallTimeMs, 0);
        }
        return 0;
    }

    _openVideoLossRegion(reason) {
        if (this._activeVideoLossRegion) {
            if (reason && this._activeVideoLossRegion.reason !== reason) {
                this._activeVideoLossRegion.reason = reason;
            }
            this._activeVideoLossRegion.endMs = this._getElapsedMsForVideoLossRegion();
            return;
        }

        const startedAtMs = this._getElapsedMsForVideoLossRegion();
        this._activeVideoLossRegion = {
            startMs: startedAtMs,
            endMs: startedAtMs,
            reason: reason || VIDEO_COMPOSER_LOSS_REASON_NO_DRAWABLE_FRAME
        };
        this._videoLossRegions.push(this._activeVideoLossRegion);
    }

    _closeVideoLossRegion() {
        if (!this._activeVideoLossRegion) {
            return;
        }
        this._activeVideoLossRegion.endMs = Math.max(this._getElapsedMsForVideoLossRegion(), this._activeVideoLossRegion.startMs);
        this._activeVideoLossRegion = null;
    }

    _scheduleNextFrame() {
        this._animationFrameId = window.requestAnimationFrame(() => {
            this._drawCurrentFrame();
            this._scheduleNextFrame();
        });
    }

    _startRenderLoop() {
        this.stopRenderingLoop();
        this._scheduleNextFrame();
    }

    stopRenderingLoop() {
        if (this._pendingSourceFrameCallbackId !== null && this._videoEl && this._videoEl.cancelVideoFrameCallback) {
            this._videoEl.cancelVideoFrameCallback(this._pendingSourceFrameCallbackId);
        }
        if (this._animationFrameId !== null) {
            window.cancelAnimationFrame(this._animationFrameId);
        }
        this._pendingSourceFrameCallbackId = null;
        this._animationFrameId = null;
    }

    getHealth() {
        if (this._activeVideoLossRegion) {
            this._activeVideoLossRegion.endMs = Math.max(this._getElapsedMsForVideoLossRegion(), this._activeVideoLossRegion.startMs);
        }
        return {
            sourceDriftMs: this._sourceDriftMs,
            drawnFrameCount: this._drawnFrameCount,
            frozenFrameCount: this._frozenFrameCount,
            videoLossRegions: this._videoLossRegions.map(function(region) {
                return Object.assign({}, region);
            }),
            latestSourceFrameMediaTimeMs: this._latestSourceFrameMediaTimeMs,
            latestSourceFrameWallTimeMs: this._latestSourceFrameWallTimeMs
        };
    }

    clear() {
        this.stopRenderingLoop();
        this._stopSourceFrameMonitor();
        if (this._videoEl && this._videoEl.pause) {
            this._videoEl.pause();
        }
        if (this._videoEl) {
            this._videoEl.srcObject = null;
            if (this._videoMountedToDom && this._videoEl.parentNode) {
                this._videoEl.parentNode.removeChild(this._videoEl);
            }
            this._videoMountedToDom = false;
        }
        stopMediaStreamTracks(this._sourceStream);
        this._sourceStream = null;
        if (this._canvasTrack) {
            try {
                this._canvasTrack.stop();
            } catch (_) {
                // Ignore canvas track cleanup failures.
            }
        }
    }
}

const TrebbleRecorder = Backbone.Model.extend({

    initialize: function(options) {
        this._useMediaRecorderForAudioRecording = options.useMediaRecorderForAudioRecording;
        this._processor = options.processor;
        this._nodeAlreadyConnected = options.nodeAlreadyConnected;
        this._stream = options.stream;
        this._previewStream = this._stream;
        this._fileName = options.fileName;
        this._mediaType = options.mediaType === "video" ? "video" : "audio";
        this._supportedMimeType = getSupportedRecorderMimeInfo(this._mediaType);
        this._recordingId = options.recordingId || this._fileName || (new Date().getTime().toString());
        this._originRoute = options.originRoute || null;
        this._originFlow = options.originFlow || null;
        this._recordingUserId = options.userId || null;
        this._durableDraftStore = BrowserRecordingDraftStore.getInstance();
        this._durableDraftsEnabled = !!(options.enableDurableDrafts && this._durableDraftStore && this._durableDraftStore.isEnabled && this._durableDraftStore.isEnabled());
        this._draftInitializationPromise = Promise.resolve(null);
        this._recordingHealth = {
            mediaType: this._mediaType,
            chunksStored: 0,
            bytesStored: 0,
            durableBytesStored: 0,
            memoryFallbackBytes: 0,
            usedMemoryFallback: false,
            partialRecovery: false,
            usesOpfs: false,
            storageBackend: null,
            hasStarted: false,
            startedAt: null,
            endedAt: null,
            workerInitError: null,
            memoryFallbackError: null
        };
        this._videoSourceSwitchCount = 0;
        this._nonFatalRecordingError = null;
        this._videoRecordingComposer = null;
        this._audioTrackStabilizer = null;
        this._sourceTrackLossMonitor = new BrowserMediaTrackLossMonitor(this._previewStream || this._stream);
        this._lastVideoComposerHealth = null;
        this._lastAudioTrackStabilizerHealth = null;
        this._lastSourceTrackLossHealth = null;
        this._pendingChunkAppendPromises = [];

        // Keep normal recordings on the browser's original audio/video stream;
        // the canvas composer can drift under rendering pressure.
        if (options.useVideoRecordingComposer && this._mediaType === "video" && this._supportedMimeType) {
            try {
                this._videoRecordingComposer = new BrowserVideoRecordingComposer(this._stream);
                this._previewStream = this._stream;
                this._stream = this._videoRecordingComposer.getRecordingStream();
            } catch (error) {
                this._videoRecordingComposer = null;
                console.warn("Unable to initialize the stable browser video recording stream", error);
            }
        }

        if (this._mediaType === "video" && this._supportedMimeType && this._stream && this._stream.getAudioTracks && this._stream.getAudioTracks().some(isTrackLive) && options.useAudioTrackStabilizer === true) {
            try {
                this._audioTrackStabilizer = new BrowserAudioTrackStabilizer(this._stream);
                this._stream = this._audioTrackStabilizer.getRecordingStream();
            } catch (error) {
                this._audioTrackStabilizer = null;
                console.warn("Unable to initialize the stable browser audio recording track", error);
            }
        }

        window._browserAudioStream = this._stream;

        if (this._supportedMimeType && ((this._mediaType === "video") || this._useMediaRecorderForAudioRecording)) {
            const mediaRecorderOptions = buildMediaRecorderOptions(this._supportedMimeType, this._mediaType, this._stream);
            this._mediaRecorder = createMediaRecorderForStream(this._stream, mediaRecorderOptions);
            this._durableRecordedMediaBuffer = new BrowserRecordingBinaryStoreClient({
                recordingId: this._recordingId,
                fileName: this._fileName,
                mimeType: this._supportedMimeType
            });
            this._bindMediaRecorderEvents();
        } else {
            this._supportedMimeType = "audio/wav";
            this._input = window._trebbleAudioContext.createMediaStreamSource(this._stream);
            this._recorderObj = new Recorder(this._input, {
                processor: this._processor,
                numChannels: 1,
                nodeAlreadyConnected: this._nodeAlreadyConnected
            });
        }
    },

    _bindMediaRecorderEvents: function() {
        this._mediaRecorder.ondataavailable = (async (event) => {
            if (!event || !event.data || event.data.size <= 0) {
                return;
            }

            this._recordingHealth.chunksStored = this._recordingHealth.chunksStored + 1;
            this._recordingHealth.bytesStored = this._recordingHealth.bytesStored + event.data.size;

            const appendPromise = this._durableRecordedMediaBuffer.appendChunk(event.data);
            this._pendingChunkAppendPromises.push(appendPromise);

            try {
                await this._draftInitializationPromise.catch(function() {
                    return null;
                });
                const appendResult = await appendPromise;
                if (appendResult) {
                    this._recordingHealth.storageBackend = appendResult.storageBackend || null;
                    this._recordingHealth.usesOpfs = appendResult.storageBackend === RECORDING_DRAFT_STORAGE_BACKENDS.OPFS;
                    if (appendResult.totalBytesWritten) {
                        this._recordingHealth.bytesStored = appendResult.totalBytesWritten;
                    }
                    if (typeof appendResult.durableBytesWritten === "number") {
                        this._recordingHealth.durableBytesStored = appendResult.durableBytesWritten;
                    }
                    if (typeof appendResult.memoryFallbackBytes === "number") {
                        this._recordingHealth.memoryFallbackBytes = appendResult.memoryFallbackBytes;
                    }
                    if (appendResult.memoryFallbackActive) {
                        this._recordingHealth.usedMemoryFallback = true;
                        this._recordingHealth.partialRecovery = appendResult.partialRecovery === true;
                    }
                    if (appendResult.memoryFallbackError) {
                        this._recordingHealth.memoryFallbackError = appendResult.memoryFallbackError;
                    }
                    if (appendResult.workerInitError) {
                        this._recordingHealth.workerInitError = appendResult.workerInitError;
                    }
                    if (this._durableDraftsEnabled) {
                        this._durableDraftStore.recordChunkPersisted(this._recordingId, {
                            storageBackend: appendResult.storageBackend,
                            totalBytesWritten: appendResult.totalBytesWritten,
                            durableBytesWritten: appendResult.durableBytesWritten,
                            memoryFallbackBytes: appendResult.memoryFallbackBytes,
                            memoryFallbackActive: appendResult.memoryFallbackActive,
                            partialRecovery: appendResult.partialRecovery,
                            memoryFallbackError: appendResult.memoryFallbackError,
                            durationMs: this._recordingHealth.startedAt ? Math.max(Date.now() - this._recordingHealth.startedAt, 0) : 0
                        }).catch(function(error) {
                            console.warn("Unable to persist the durable recording chunk manifest update", error);
                        });
                    }
                }
            } catch (error) {
                this._recordingError = error;
            } finally {
                this._pendingChunkAppendPromises = this._pendingChunkAppendPromises.filter(function(pendingPromise) {
                    return pendingPromise !== appendPromise;
                });
            }
        }).bind(this);

        this._mediaRecorder.onerror = (function(event) {
            this._recordingError = (event && event.error) ? event.error : event;
        }).bind(this);
    },

    canInputMonitoringTurnedOn: function() {
        return !!this._input;
    },

    turnOnInputMonitoring: function(inputMonitoringOn) {
        if (!this._input) {
            return;
        }

        if (inputMonitoringOn && !this._isInputMonitoringOn) {
            this._input.connect(window._trebbleAudioContext.destination);
            this._isInputMonitoringOn = true;
        } else if (this._isInputMonitoringOn) {
            this._input.disconnect(window._trebbleAudioContext.destination);
            this._isInputMonitoringOn = false;
        }
    },

    _runPromiseWithTimeout: function(promise, timeoutInMilliseconds, timeoutMessage) {
        return new Promise(function(resolve, reject) {
            let settled = false;
            const timeoutApi = typeof window !== "undefined" && window.setTimeout ? window : globalThis;
            const timeoutId = timeoutApi.setTimeout(function() {
                if (settled) {
                    return;
                }
                settled = true;
                reject(new Error(timeoutMessage));
            }, timeoutInMilliseconds);

            Promise.resolve(promise).then(function(value) {
                if (settled) {
                    return;
                }
                settled = true;
                timeoutApi.clearTimeout(timeoutId);
                resolve(value);
            }).catch(function(error) {
                if (settled) {
                    return;
                }
                settled = true;
                timeoutApi.clearTimeout(timeoutId);
                reject(error);
            });
        });
    },

    _resetMediaRecorderForCurrentStream: function() {
        if (!this._supportedMimeType || !(this._mediaType === "video" || this._useMediaRecorderForAudioRecording)) {
            return;
        }
        if (this._mediaRecorder && this._mediaRecorder.state !== "inactive") {
            throw new Error("Cannot update the recording stream after MediaRecorder has started.");
        }

        const mediaRecorderOptions = buildMediaRecorderOptions(this._supportedMimeType, this._mediaType, this._stream);
        this._mediaRecorder = createMediaRecorderForStream(this._stream, mediaRecorderOptions);
        this._bindMediaRecorderEvents();
    },

    _fallbackToOriginalAudioTrackForRecording: function(reason) {
        if (!this._audioTrackStabilizer) {
            return;
        }

        const fallbackStream = this._audioTrackStabilizer.getSourceStream ? this._audioTrackStabilizer.getSourceStream() : null;
        if (!(fallbackStream && fallbackStream.getAudioTracks && fallbackStream.getAudioTracks().some(isTrackLive))) {
            throw reason || new Error("Unable to start the stable browser audio recording track.");
        }

        this._lastAudioTrackStabilizerHealth = Object.assign({}, this._audioTrackStabilizer.getHealth ? this._audioTrackStabilizer.getHealth() : {}, {
            enabled: false,
            fallbackToOriginalAudioTrack: true,
            fallbackReason: reason && reason.message ? reason.message : String(reason || "")
        });
        this._audioTrackStabilizer.clear({
            preserveSourceStream: true
        });
        this._audioTrackStabilizer = null;
        this._stream = fallbackStream;
        this._resetMediaRecorderForCurrentStream();
    },

    _prepareAudioTrackStabilizerForRecording: function() {
        if (!this._audioTrackStabilizer) {
            return Promise.resolve();
        }

        return this._runPromiseWithTimeout(
            this._audioTrackStabilizer.resume(),
            AUDIO_STABILIZER_START_TIMEOUT_IN_MILLISECONDS,
            "Timed out while starting the stable browser audio recording track."
        ).then((function() {
            if (!this._audioTrackStabilizer || this._audioTrackStabilizer.isRunning()) {
                return;
            }
            throw new Error("The stable browser audio recording track did not start.");
        }).bind(this)).catch((function(error) {
            console.warn("Falling back to the original browser audio track", error);
            this._fallbackToOriginalAudioTrackForRecording(error);
        }).bind(this));
    },

    record: function() {
        return this._prepareAudioTrackStabilizerForRecording().then((async function() {
            this._stopPromise = null;
            this._finalizedFilePromise = null;
            this._recordingError = null;
            this._nonFatalRecordingError = null;
            this._pendingChunkAppendPromises = [];
            this._recordingHealth.chunksStored = 0;
            this._recordingHealth.bytesStored = 0;
            this._recordingHealth.durableBytesStored = 0;
            this._recordingHealth.memoryFallbackBytes = 0;
            this._recordingHealth.usedMemoryFallback = false;
            this._recordingHealth.partialRecovery = false;
            this._recordingHealth.usesOpfs = false;
            this._recordingHealth.storageBackend = null;
            this._recordingHealth.endedAt = null;
            this._recordingHealth.workerInitError = null;
            this._recordingHealth.memoryFallbackError = null;
            if (this._durableRecordedMediaBuffer && this._durableRecordedMediaBuffer.resetPersistedState) {
                await this._durableRecordedMediaBuffer.resetPersistedState();
            }
            this._recordingHealth.hasStarted = true;
            this._recordingHealth.startedAt = Date.now();
            if (this._sourceTrackLossMonitor) {
                this._sourceTrackLossMonitor.start(this._recordingHealth.startedAt);
            }
            this._draftInitializationPromise = Promise.resolve().then((function() {
                if (!this._durableDraftsEnabled) {
                    return null;
                }

                return this._durableDraftStore.createDraft({
                    recordingId: this._recordingId,
                    fileName: this._fileName,
                    mimeType: this._supportedMimeType,
                    originRoute: this._originRoute,
                    originFlow: this._originFlow,
                    userId: this._recordingUserId
                });
            }).bind(this)).catch(function(error) {
                console.warn("Unable to initialize the durable browser recording draft", error);
                return null;
            });

            if (this._mediaRecorder) {
                this._mediaRecorder.start(RECORDING_TIMESLICE_IN_MILLISECONDS);
            } else if (this._recorderObj) {
                this._recorderObj.record();
            }
        }).bind(this));
    },

    _flushPendingMediaRecorderData: function(timeoutInMilliseconds) {
        if (!this._mediaRecorder || !this._mediaRecorder.requestData || this._mediaRecorder.state === "inactive") {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let settled = false;
            let timeoutId = null;

            const cleanup = () => {
                if (timeoutId !== null) {
                    window.clearTimeout(timeoutId);
                    timeoutId = null;
                }
                this._mediaRecorder.removeEventListener("dataavailable", handleDataAvailable);
            };

            const finish = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                resolve();
            };

            const handleDataAvailable = (event) => {
                if (event && event.data && event.data.size > 0) {
                    finish();
                }
            };

            timeoutId = window.setTimeout(finish, timeoutInMilliseconds || 750);
            this._mediaRecorder.addEventListener("dataavailable", handleDataAvailable);

            try {
                this._mediaRecorder.requestData();
            } catch (_) {
                finish();
            }
        });
    },

    stop: function() {
        if (this._mediaRecorder) {
            if (this._finalizedFilePromise) {
                return this._finalizedFilePromise;
            }

            if (!this._stopPromise) {
                this._stopPromise = new Promise((resolve, reject) => {
                    const finalizeRecording = async () => {
                        try {
                            await this._draftInitializationPromise.catch(function() {
                                return null;
                            });
                            if (this._pendingChunkAppendPromises.length > 0) {
                                await waitForPromisesToSettle(this._pendingChunkAppendPromises.slice());
                            }

                            if (this._recordingError && !this._shouldIgnoreMediaRecorderError(this._recordingError)) {
                                throw this._recordingError;
                            }

                            if (this._recordingError && this._shouldIgnoreMediaRecorderError(this._recordingError)) {
                                this._nonFatalRecordingError = this._recordingError;
                            }

                            const finalizeResult = await this._durableRecordedMediaBuffer.finalize();
                            const finalizedFile = finalizeResult && finalizeResult.file ? finalizeResult.file : null;
                            this._recordingHealth.storageBackend = finalizeResult && finalizeResult.storageBackend ? finalizeResult.storageBackend : this._recordingHealth.storageBackend;
                            this._recordingHealth.usesOpfs = this._recordingHealth.storageBackend === RECORDING_DRAFT_STORAGE_BACKENDS.OPFS;
                            if (finalizeResult && finalizeResult.totalBytesWritten) {
                                this._recordingHealth.bytesStored = finalizeResult.totalBytesWritten;
                            }
                            if (finalizeResult && typeof finalizeResult.durableBytesWritten === "number") {
                                this._recordingHealth.durableBytesStored = finalizeResult.durableBytesWritten;
                            }
                            if (finalizeResult && typeof finalizeResult.memoryFallbackBytes === "number") {
                                this._recordingHealth.memoryFallbackBytes = finalizeResult.memoryFallbackBytes;
                            }
                            if (finalizeResult && finalizeResult.memoryFallbackActive) {
                                this._recordingHealth.usedMemoryFallback = true;
                                this._recordingHealth.partialRecovery = finalizeResult.partialRecovery === true;
                            }
                            if (finalizeResult && finalizeResult.memoryFallbackError) {
                                this._recordingHealth.memoryFallbackError = finalizeResult.memoryFallbackError;
                            }
                            if (finalizeResult && finalizeResult.workerInitError) {
                                this._recordingHealth.workerInitError = finalizeResult.workerInitError;
                            }
                            this._recordingHealth.endedAt = Date.now();
                            if (this._durableDraftsEnabled && this._durableDraftStore) {
                                await Promise.resolve(this._durableDraftStore.markStoppedReadyToUpload(this._recordingId, {
                                    status: RECORDING_DRAFT_STATUSES.STOPPED_READY_TO_UPLOAD,
                                    storageBackend: this._recordingHealth.storageBackend,
                                    fileName: this._fileName,
                                    mimeType: finalizedFile && finalizedFile.type ? finalizedFile.type : this._supportedMimeType,
                                    bytesStored: this._recordingHealth.bytesStored,
                                    durableBytesStored: this._recordingHealth.durableBytesStored,
                                    memoryFallbackBytes: this._recordingHealth.memoryFallbackBytes,
                                    usedMemoryFallback: this._recordingHealth.usedMemoryFallback,
                                    partialRecovery: this._recordingHealth.partialRecovery,
                                    lastPersistenceError: this._recordingHealth.memoryFallbackError,
                                    durationMs: this._recordingHealth.startedAt ? Math.max(this._recordingHealth.endedAt - this._recordingHealth.startedAt, 0) : 0
                                })).catch(function(error) {
                                    console.warn("Unable to persist the stopped-ready durable draft checkpoint", error);
                                });
                            }
                            this._finalizedFilePromise = Promise.resolve(finalizedFile);
                            this._stopTracks();
                            resolve(finalizedFile);
                        } catch (error) {
                            if (this._durableDraftsEnabled && this._durableDraftStore) {
                                await Promise.resolve(this._durableDraftStore.markRecoverableFailure(this._recordingId, error, {
                                    bytesStored: this._recordingHealth.bytesStored,
                                    storageBackend: this._recordingHealth.storageBackend,
                                    durationMs: this._recordingHealth.startedAt ? Math.max(Date.now() - this._recordingHealth.startedAt, 0) : 0
                                })).catch(function(draftError) {
                                    console.warn("Unable to mark the failed browser recording as recoverable", draftError);
                                });
                            }
                            this._stopTracks();
                            reject(error);
                        }
                    };

                    Promise.resolve().then(async () => {
                        if (this._durableDraftsEnabled && this._durableDraftStore) {
                            await Promise.resolve(this._durableDraftStore.markRecordingStopping(this._recordingId, {
                                bytesStored: this._recordingHealth.bytesStored,
                                storageBackend: this._recordingHealth.storageBackend,
                                durationMs: this._recordingHealth.startedAt ? Math.max(Date.now() - this._recordingHealth.startedAt, 0) : 0
                            })).catch(function(error) {
                                console.warn("Unable to persist the recording-stopping durable draft checkpoint", error);
                            });
                        }
                        if (this._mediaRecorder.state !== "inactive" && this._recordingHealth.chunksStored === 0) {
                            await this._flushPendingMediaRecorderData(RECORDING_TIMESLICE_IN_MILLISECONDS);
                        }
                    }).then(() => {
                        this._mediaRecorder.addEventListener("stop", finalizeRecording, { once: true });
                        try {
                            if (this._mediaRecorder.state !== "inactive") {
                                if (this._mediaRecorder.requestData) {
                                    try {
                                        this._mediaRecorder.requestData();
                                    } catch (_) {
                                        // Ignore flush errors and let stop continue.
                                    }
                                }
                                this._mediaRecorder.stop();
                            } else {
                                finalizeRecording();
                            }
                        } catch (error) {
                            Promise.resolve().then(async () => {
                                if (this._durableDraftsEnabled && this._durableDraftStore) {
                                    await Promise.resolve(this._durableDraftStore.markRecoverableFailure(this._recordingId, error, {
                                        bytesStored: this._recordingHealth.bytesStored,
                                        storageBackend: this._recordingHealth.storageBackend,
                                        durationMs: this._recordingHealth.startedAt ? Math.max(Date.now() - this._recordingHealth.startedAt, 0) : 0
                                    })).catch(function(draftError) {
                                        console.warn("Unable to mark the failed browser recording as recoverable", draftError);
                                    });
                                }
                                this._stopTracks();
                            }).then(function() {
                                reject(error);
                            }).catch(function() {
                                reject(error);
                            });
                        }
                    }).catch(reject);
                });
            }

            return this._stopPromise;
        }

        if (this._recorderObj) {
            this._recorderObj.stop();
            this._recordingHealth.endedAt = Date.now();
            this._stopTracks();
        }
        return Promise.resolve(null);
    },

    exportWAV: function(onSuccess, onError) {
        if (this._mediaRecorder) {
            const finalizedFilePromise = this._finalizedFilePromise || this._stopPromise || Promise.reject(new Error("The recording has not been stopped yet."));
            return finalizedFilePromise.then(function(finalizedFile) {
                onSuccess(finalizedFile);
                return finalizedFile;
            }).catch(function(error) {
                if (onError) {
                    onError(error);
                } else {
                    throw error;
                }
            });
        }

        if (this._recorderObj) {
            this._recorderObj.exportWAV(onSuccess);
        }
    },

    getStream: function() {
        return this._stream;
    },

    getPreviewStream: function() {
        if (this._previewStream) {
            return this._previewStream;
        }
        return this._stream;
    },

    getMediaType: function() {
        return this._mediaType;
    },

    isVideo: function() {
        return this._mediaType === "video";
    },

    getMimeType: function() {
        return this._supportedMimeType;
    },

    getRecordingId: function() {
        return this._recordingId;
    },

    getRecordingHealth: function() {
        return Object.assign({}, this._recordingHealth, {
            videoSourceSwitchCount: this._videoSourceSwitchCount,
            videoComposer: this._videoRecordingComposer && this._videoRecordingComposer.getHealth ? this._videoRecordingComposer.getHealth() : this._lastVideoComposerHealth,
            audioTrackStabilizer: this._audioTrackStabilizer && this._audioTrackStabilizer.getHealth ? this._audioTrackStabilizer.getHealth() : this._lastAudioTrackStabilizerHealth,
            mediaTrackLoss: this._sourceTrackLossMonitor && this._sourceTrackLossMonitor.getHealth ? this._sourceTrackLossMonitor.getHealth() : this._lastSourceTrackLossHealth,
            nonFatalRecordingError: this._nonFatalRecordingError ? {
                name: this._nonFatalRecordingError.name,
                message: this._nonFatalRecordingError.message
            } : null
        });
    },

    markVideoSourceSwitched: function() {
        this._videoSourceSwitchCount = this._videoSourceSwitchCount + 1;
    },

    replaceVideoSourceStream: function(sourceStream) {
        if (!(this._mediaType === "video" && this._videoRecordingComposer)) {
            return Promise.reject(new Error("The active recording does not support live video source switching."));
        }

        return this._videoRecordingComposer.replaceSourceStream(sourceStream).then((function(updatedRecordingStream) {
            this.markVideoSourceSwitched();
            this._previewStream = sourceStream;
            if (this._sourceTrackLossMonitor) {
                this._sourceTrackLossMonitor.replaceSourceStream(sourceStream);
            }
            return updatedRecordingStream;
        }).bind(this));
    },

    _shouldIgnoreMediaRecorderError: function(error) {
        if (!(this._mediaType === "video" && this._videoSourceSwitchCount > 0 && error)) {
            return false;
        }

        const errorMessage = ((error && error.message) || (error && error.name) || String(error) || "").toLowerCase();
        return errorMessage.indexOf("tracks in mediastream were removed") >= 0;
    },

    getDefaultVideoConstraints: function() {
        return Object.assign({}, DEFAULT_VIDEO_CONSTRAINTS);
    },

    _snapshotAuxiliaryRecordingHealth: function() {
        if (this._videoRecordingComposer && this._videoRecordingComposer.getHealth) {
            this._lastVideoComposerHealth = this._videoRecordingComposer.getHealth();
        }
        if (this._audioTrackStabilizer && this._audioTrackStabilizer.getHealth) {
            this._lastAudioTrackStabilizerHealth = this._audioTrackStabilizer.getHealth();
        }
        if (this._sourceTrackLossMonitor) {
            this._lastSourceTrackLossHealth = this._sourceTrackLossMonitor.finalize();
        }
    },

    _stopTracks: function() {
        this._snapshotAuxiliaryRecordingHealth();
        if (this._sourceTrackLossMonitor) {
            this._sourceTrackLossMonitor.clear();
            this._sourceTrackLossMonitor = null;
        }
        if (this._audioTrackStabilizer) {
            this._audioTrackStabilizer.clear();
            this._audioTrackStabilizer = null;
        }
        if (this._videoRecordingComposer) {
            this._videoRecordingComposer.clear();
            this._videoRecordingComposer = null;
        }
        this._previewStream = null;
        if (this._stream && this._stream.getTracks) {
            this._stream.getTracks().forEach(function(track) {
                try {
                    track.stop();
                } catch (_) {
                    // Ignore track cleanup failures.
                }
            });
        }
    },

    release: function() {
        if (this._mediaRecorder) {
            if (this._durableRecordedMediaBuffer && this._durableRecordedMediaBuffer.release) {
                this._durableRecordedMediaBuffer.release().catch(function(error) {
                    console.warn("Unable to release the durable browser recording buffer", error);
                });
            }
            this._stopTracks();
            return;
        }

        if (this._recorderObj && this._recorderObj.clear) {
            this._recorderObj.clear();
        }
        this._stopTracks();
    },

    deletePersistedDraft: function() {
        if (this._durableRecordedMediaBuffer && this._durableRecordedMediaBuffer.clear) {
            return this._durableRecordedMediaBuffer.clear();
        }

        return Promise.resolve();
    },

    clear: function() {
        this.release();
    }

});

TrebbleRecorder.getSupportedRecorderMimeInfo = getSupportedRecorderMimeInfo;

export default TrebbleRecorder;
